import { z } from 'zod';
import { ArtistsWorkedWithSchema } from 'src/shared/types/teacher';

// Schema per gli strumenti
const InstrumentSchema = z.object({
  name: z.string(),
  iconUrl: z.string().optional(),
});

// Schema per il corso
const CourseSchema = z.object({
  documentId: z.string(),
  id: z.string(),
  title: z.string(),
  type: z.enum(['percorso', 'serie', 'masterclass']),
  description: z.string(),
  genres: z.array(
    z.object({ id: z.number(), name: z.string(), documentId: z.string().optional() }),
  ),
  videoCount: z.number(),
  playbackId: z.nullable(z.string()).optional(),
  courseImageUrl: z.string(),
  teachers: z.array(
    z.object({
      displayName: z.string(),
      avatarUrl: z.string(),
    }),
  ),
  isCourseStarted: z.boolean().optional(),
  courseHighlight: z.enum(['highlightedCourses', 'regularCourses']).optional(),
  level: z.enum(['Da zero', 'Principiante', 'Intermedio', 'Avanzato', 'Per tutti']).optional(),
  categoryTags: z
    .array(
      z.union([
        z.literal('In evidenza'),
        z.literal('Impara da zero'),
        z.literal('Ultime uscite'),
        z.literal('Più seguiti'),
        z.literal('Masterclass'),
        z.string(),
      ]),
    )
    .optional(),
  courseType: z.enum(['masterclass', 'percorsi', 'serie']).optional(),
  highlighted: z.boolean().optional(),
  isLocked: z.boolean().optional(),
  progressValue: z.number().optional(),
  lessonCount: z.number().optional(),
  duration: z.string().optional(),
  videoUrl: z.string().url().optional(),
  logoUrl: z.string().url().optional(),
  coursePercentage: z.number().optional(),
});

// Schema per `TeacherItem`
const TeacherItemSchema = z.object({
  id: z.string(),
  documentId: z.string(),
  displayName: z.string(),
  profilePictureUrl: z.string().url().optional(),
  biography: z.string(),
  instruments: z.array(InstrumentSchema),
  mainSubject: z.string(),
  artistsWorkedWith: z.nullable(ArtistsWorkedWithSchema),
  topViewedCourses: z.array(CourseSchema),
  allCourses: z.any(), // TODO: Set correct typing based on the TS interfaces
  musicalGenres: z.array(
    z.object({
      name: z.string(),
    }),
  ),
  series: z
    .array(
      z.object({
        title: z.string(),
        description: z.string().optional(),
        numberOfVideos: z.number().optional(),
        image: z.string().optional(),
      }),
    )
    .optional(), // Aggiunto series
  masterclass: z
    .array(
      z.object({
        title: z.string(),
        description: z.string().optional(),
        numberOfVideos: z.number().optional(),
        image: z.string().optional(),
      }),
    )
    .optional(), // Aggiunto masterclass
  lessons: z
    .array(
      z.object({
        title: z.string(),
        duration: z.string(),
      }),
    )
    .optional(), // Aggiunto lessons
  learningPaths: z
    .array(
      z.object({
        title: z.string(),
        description: z.string().optional(),
        numberOfSteps: z.number().optional(),
        image: z.string().optional(),
      }),
    )
    .optional(), // Aggiunto learningPaths
});

// Schema per `topVideos`
const TopTeacherVideosSchema = z.array(
  z.object({
    documentId: z.string(),
    id: z.number(),
    title: z.string(),
    description: z.string(),
    type: z.enum(['Masterclass', 'Percorsi', 'Serie']).optional(),
    courseType: z.enum(['Masterclass', 'Percorsi', 'Serie']).optional(),
    numberOfVideos: z.number().optional(),
    numberOfLessons: z.number().optional(),
    requirements: z.string().optional(),
    progress: z.number().optional(),
    isHighlighted: z.boolean().optional(),
    duration: z.number(),
    isPurchasable: z.boolean(),
    playbackId: z.nullable(z.string()).optional(),
    thumbnail: z.object({ url: z.string().optional() }).nullable().optional(),
    teacherProfiles: z
      .array(
        z.object({
          displayName: z.string(),
          profilePicture: z.object({ url: z.string().optional() }).nullable().optional(),
        }),
      )
      .nullable(),
    skillLevel: z.object({ name: z.string().optional() }).optional(),
    topics: z.array(z.object({ name: z.string() })).optional(),
    lessons: z
      .array(
        z.object({
          title: z.string().nullable(),
          duration: z.number(),
          lessonProgresses: z.object({ progress_status: z.string() }).optional(),
        }),
      )
      .optional(),
  }),
);

export type TopTeacherVideos = z.infer<typeof TopTeacherVideosSchema>;

// Schema per `TeacherListItem`
const TeacherListItemSchema = z.object({
  documentId: z.string(),
  id: z.string(),
  displayName: z.string(),
  instruments: z.array(InstrumentSchema),
  imageUrl: z.string().url(),
});

// Schema per la lista di insegnanti
const TeacherListSchema = z.array(TeacherListItemSchema);

// Schema per `TeacherTeacherProfileAttributes`
const TeacherTeacherProfileAttributesSchema = z.object({
  displayName: z.string(),
  biography: z.string().optional(),
  instruments: z.array(InstrumentSchema).optional(),
  mainSubject: z.string().optional(),
  artistsWorkedWith: z.array(z.string()).optional(),
  createdAt: z.string(),
  updatedAt: z.string(),
  series: z
    .array(
      z.object({
        id: z.number(),
        attributes: z.object({
          title: z.string(),
          description: z.string().optional(),
          numberOfVideos: z.number().optional(),
          image: z.string().optional(),
        }),
      }),
    )
    .optional(),
  learningPaths: z
    .array(
      z.object({
        id: z.number(),
        attributes: z.object({
          title: z.string(),
          description: z.string().optional(),
          numberOfSteps: z.number().optional(),
          image: z.string().optional(),
        }),
      }),
    )
    .optional(),
});

// Schema per `TeacherTeacherProfile`
const TeacherTeacherProfileSchema = z.object({
  id: z.number(),
  attributes: TeacherTeacherProfileAttributesSchema,
});

// Esportazioni principali
export {
  TeacherItemSchema,
  TeacherListItemSchema,
  TeacherListSchema,
  TeacherTeacherProfileSchema,
  CourseSchema,
  InstrumentSchema,
  TopTeacherVideosSchema,
};

// Tipi derivati da Zod
export type TeacherItem = z.infer<typeof TeacherItemSchema>;
export type TeacherTopVideos = z.infer<typeof TopTeacherVideosSchema>;
export type TeacherListItem = z.infer<typeof TeacherListItemSchema>;
export type TeacherList = z.infer<typeof TeacherListSchema>;
export type TeacherTeacherProfile = z.infer<typeof TeacherTeacherProfileSchema>;
export type Course = z.infer<typeof CourseSchema>;
export type Instrument = z.infer<typeof InstrumentSchema>;
